import React, { useState } from 'react';
import { 
  Eye, 
  Edit3, 
  Trash2, 
  Share2,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  Video,
  Image as ImageIcon,
  FileText,
  Calendar,
  RefreshCw,
  XCircle,
  Star
} from 'lucide-react';

/**
 * Progress ring — extracted out of CampaignCardV2 so React does not remount it
 * on every parent re-render (fixes react/no-unstable-nested-components).
 */
const ProgressRing = ({ progress, size = 48, strokeWidth = 4, ringColorClass = 'stroke-emerald-500' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-slate-200 dark:text-slate-700"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        className={ringColorClass}
        style={{
          strokeDasharray: circumference,
          strokeDashoffset: offset,
          transition: 'stroke-dashoffset 0.5s ease'
        }}
      />
    </svg>
  );
};

/**
 * Modern campaign card component with glassmorphism effect
 * Features progress ring, status indicators, and quick actions
 * Supports theme colors: green, purple, blue
 */
const CampaignCardV2 = ({ 
  campaign,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
  onShare,
  onRetry,
  onKill,
  onToggleFavorite,
  isSelected = false,
  onSelect,
  colorTheme = 'green' // 'green', 'purple', 'blue'
}) => {
  // State for image loading
  const [imageError, setImageError] = useState(false);
  const isFavorite = !!campaign.is_favorite;

  // Extract data from campaign - handle both flat and nested product structure
  const id = campaign.id;
  const product = campaign.product || {};
  const product_name = product.product_name || campaign.product_name || 'Untitled Campaign';
  const product_description = product.product_description || campaign.product_description || '';
  const product_image = product.product_image || campaign.product_image;
  const category = product.category || campaign.category || '';
  const generation_status = campaign.generation_status || 'completed';
  const generation_progress = campaign.generation_progress || campaign.progress || 0;
  const created_at = campaign.created_at;
  const generated_ads = campaign.generated_ads || [];
  
  // Get content types from generated ads
  const content_types = generated_ads.map(ad => ad.ad_type).filter(Boolean);

  // Theme-aware colors
  const themeColors = {
    green: {
      ring: 'ring-emerald-500',
      border: 'hover:border-emerald-500/30',
      checkbox: 'text-emerald-500 focus:ring-emerald-500',
      button: 'bg-emerald-500 hover:bg-emerald-600',
      buttonLight: 'bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-500/20 dark:hover:bg-emerald-500/30 text-emerald-700 dark:text-emerald-300',
      generating: {
        ringColor: 'stroke-emerald-500',
        color: 'text-emerald-500',
        bg: 'bg-emerald-500/10'
      }
    },
    purple: {
      ring: 'ring-purple-500',
      border: 'hover:border-purple-500/30',
      checkbox: 'text-purple-500 focus:ring-purple-500',
      button: 'bg-purple-500 hover:bg-purple-600',
      buttonLight: 'bg-purple-100 hover:bg-purple-200 dark:bg-purple-500/20 dark:hover:bg-purple-500/30 text-purple-700 dark:text-purple-300',
      generating: {
        ringColor: 'stroke-purple-500',
        color: 'text-purple-500',
        bg: 'bg-purple-500/10'
      }
    },
    blue: {
      ring: 'ring-blue-500',
      border: 'hover:border-blue-500/30',
      checkbox: 'text-blue-500 focus:ring-blue-500',
      button: 'bg-blue-500 hover:bg-blue-600',
      buttonLight: 'bg-blue-100 hover:bg-blue-200 dark:bg-blue-500/20 dark:hover:bg-blue-500/30 text-blue-700 dark:text-blue-300',
      generating: {
        ringColor: 'stroke-blue-500',
        color: 'text-blue-500',
        bg: 'bg-blue-500/10'
      }
    }
  };

  const theme = themeColors[colorTheme] || themeColors.green;

  // Status configuration
  const statusConfig = {
    completed: {
      icon: CheckCircle,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10',
      label: 'Completed',
      ringColor: 'stroke-emerald-500'
    },
    generating: {
      icon: Loader2,
      color: theme.generating.color,
      bg: theme.generating.bg,
      label: 'Generating',
      ringColor: theme.generating.ringColor,
      animate: true
    },
    queued: {
      icon: Clock,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10',
      label: 'Queued',
      ringColor: 'stroke-amber-500'
    },
    failed: {
      icon: AlertCircle,
      color: 'text-rose-500',
      bg: 'bg-rose-500/10',
      label: 'Failed',
      ringColor: 'stroke-rose-500'
    }
  };

  const status = statusConfig[generation_status] || statusConfig.completed;
  const StatusIcon = status.icon;

  // Get content type icons
  const getContentTypeIcon = (type) => {
    if (type?.includes('video')) return Video;
    if (type?.includes('image')) return ImageIcon;
    return FileText;
  };

  // Get thumbnail - check multiple possible sources
  const getThumbnail = () => {
    // 1. Try product image first (uploaded by user)
    if (product_image) return product_image;
    
    // 2. Try to find first ad with an image
    for (const ad of generated_ads) {
      if (ad.image_url) return ad.image_url;
      if (ad.imageUrl) return ad.imageUrl;
      if (ad.thumbnail) return ad.thumbnail;
    }
    
    // 3. Try to find first ad with a video (can be used as preview)
    for (const ad of generated_ads) {
      if (ad.video_url) return ad.video_url;
      if (ad.videoUrl) return ad.videoUrl;
    }
    
    // 4. Check if campaign has a thumbnail field directly
    if (campaign.thumbnail) return campaign.thumbnail;
    if (campaign.preview_image) return campaign.preview_image;
    
    return null;
  };

  const thumbnail = getThumbnail();
  
  // Check if it's a video URL (for poster handling)
  const isVideo = thumbnail && (
    thumbnail.includes('.mp4') || 
    thumbnail.includes('.webm') || 
    thumbnail.includes('.mov') ||
    thumbnail.includes('/videos/')
  );

  // Get text content for text-only ads preview
  const getTextPreview = () => {
    if (generated_ads.length === 0) return null;
    
    const firstAd = generated_ads[0];
    const title = firstAd.title || firstAd.headline || '';
    const content = firstAd.content || firstAd.body || firstAd.text || firstAd.description || '';
    const cta = firstAd.call_to_action || firstAd.cta || '';
    
    return { title, content, cta, adType: firstAd.ad_type };
  };

  const textPreview = !thumbnail ? getTextPreview() : null;
  const isTextOnlyAd = !thumbnail && textPreview && (textPreview.title || textPreview.content);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  // Check if thumbnail should show (valid URL and no error)
  const showThumbnail = thumbnail && !imageError;

  return (
    <div 
      className={`
        group relative overflow-hidden rounded-2xl
        bg-white/70 dark:bg-slate-800/50
        backdrop-blur-xl
        border border-slate-200/50 dark:border-slate-700/50
        shadow-sm hover:shadow-xl
        transition-all duration-300 ease-out
        hover:scale-[1.01] ${theme.border}
        ${isSelected ? `ring-2 ${theme.ring} border-transparent` : ''}
      `}
      data-testid={`campaign-card-${id}`}
    >
      {/* Selection checkbox */}
      {onSelect && (
        <div className="absolute top-4 left-4 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(id)}
            className={`w-5 h-5 rounded-md border-slate-300 dark:border-slate-600 
                     ${theme.checkbox} cursor-pointer`}
            data-testid={`campaign-select-${id}`}
          />
        </div>
      )}

      {/* Thumbnail / Preview section */}
      <div className="relative h-40 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 overflow-hidden">
        {/* Persistent favorite badge — visible at a glance without hover */}
        {isFavorite && (
          <div
            className="absolute top-2 right-2 z-20 w-7 h-7 rounded-full bg-yellow-400 text-white flex items-center justify-center shadow-lg"
            title="Favorite campaign"
            data-testid={`campaign-favorite-badge-${id}`}
          >
            <Star className="w-3.5 h-3.5 fill-current" />
          </div>
        )}
        {showThumbnail ? (
          isVideo ? (
            <video 
              src={thumbnail} 
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              muted
              playsInline
              onError={() => setImageError(true)}
              onLoadedData={(e) => {
                // Seek to 1 second for a better preview frame
                e.target.currentTime = 1;
              }}
            />
          ) : (
            <img 
              src={thumbnail} 
              alt={product_name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={() => setImageError(true)}
            />
          )
        ) : isTextOnlyAd ? (
          /* Text-only ad preview */
          <div className={`absolute inset-0 pt-2 pr-3 pb-3 pl-11 flex flex-col ${
            colorTheme === 'purple' ? 'bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/40 dark:to-pink-900/40' :
            colorTheme === 'blue' ? 'bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/40 dark:to-sky-900/40' :
            'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/40 dark:to-teal-900/40'
          }`}>
            {/* Text ad badge — top-right */}
            <div className="flex justify-end mb-1 shrink-0">
              <span className={`px-2 py-0.5 rounded text-[10px] font-medium inline-flex items-center gap-1 ${
                colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-500/30 text-purple-600 dark:text-purple-300' :
                colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-500/30 text-blue-600 dark:text-blue-300' :
                'bg-emerald-100 dark:bg-emerald-500/30 text-emerald-600 dark:text-emerald-300'
              }`}>
                <FileText className="w-3 h-3" />
                Text Ad
              </span>
            </div>
            
            {/* Ad preview content */}
            <div className="flex-1 flex flex-col justify-center min-h-0 overflow-hidden">
              {textPreview.title && (
                <h4 className={`font-bold text-sm leading-snug mb-1 line-clamp-2 ${
                  colorTheme === 'purple' ? 'text-purple-800 dark:text-purple-200' :
                  colorTheme === 'blue' ? 'text-blue-800 dark:text-blue-200' :
                  'text-emerald-800 dark:text-emerald-200'
                }`}>
                  {textPreview.title}
                </h4>
              )}
              {textPreview.content && (
                <p className="text-slate-600 dark:text-slate-400 text-xs line-clamp-3 leading-relaxed">
                  {textPreview.content}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-slate-400 dark:text-slate-500 text-center">
              {generation_status === 'generating' ? (
                <Loader2 className="w-12 h-12 animate-spin mx-auto" />
              ) : (
                <>
                  <ImageIcon className="w-10 h-10 mx-auto mb-2" />
                  <span className="text-xs">No preview</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Status overlay for generating campaigns */}
        {generation_status === 'generating' && (
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center">
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center mb-2">
                <ProgressRing progress={generation_progress} ringColorClass={status.ringColor} />
                <span className="absolute text-white font-bold text-sm">
                  {Math.round(generation_progress)}%
                </span>
              </div>
              <p className="text-white/80 text-sm">Generating...</p>
            </div>
          </div>
        )}

        {/* Quick actions overlay on hover */}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); onShare?.(campaign); }}
              className="p-2 bg-white/90 dark:bg-slate-800/90 rounded-lg shadow-lg 
                       hover:bg-white dark:hover:bg-slate-700 transition-colors"
              title="Share"
              data-testid={`campaign-share-${id}`}
            >
              <Share2 className="w-4 h-4 text-slate-600 dark:text-slate-300" />
            </button>
          </div>
        </div>
      </div>

      {/* Content section */}
      <div className="p-5">
        {/* Title and status */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-semibold text-slate-900 dark:text-white text-lg leading-tight line-clamp-1">
            {product_name}
          </h3>
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${status.bg} shrink-0`}>
            <StatusIcon className={`w-3.5 h-3.5 ${status.color} ${status.animate ? 'animate-spin' : ''}`} />
            <span className={`text-xs font-medium ${status.color}`}>{status.label}</span>
          </div>
        </div>

        {/* Description */}
        {product_description && (
          <p className="text-slate-500 dark:text-slate-400 text-sm line-clamp-2 mb-4">
            {product_description}
          </p>
        )}

        {/* Meta info */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Category badge */}
            {category && (
              <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-700 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300">
                {category}
              </span>
            )}
            {/* Content types */}
            <div className="flex -space-x-1">
              {content_types.slice(0, 3).map((type, idx) => {
                const TypeIcon = getContentTypeIcon(type);
                return (
                  <div 
                    key={idx}
                    className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center border-2 border-white dark:border-slate-800"
                  >
                    <TypeIcon className="w-3 h-3 text-slate-500 dark:text-slate-400" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Date */}
          <div className="flex items-center gap-1 text-slate-400 dark:text-slate-500 text-xs">
            <Calendar className="w-3.5 h-3.5" />
            {formatDate(created_at)}
          </div>
        </div>

          {/* Action Buttons - Always visible */}
        <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100 dark:border-slate-700">
          {(generation_status === 'failed' || (generation_status === 'generating' && (() => {
            // Show "Retry" on a 'generating' card if it has been stuck > 10 minutes.
            // Saves the user from the original d2ee1438 spinner-forever scenario.
            const started = campaign.generation_started_at;
            if (!started) return false;
            const ageMs = Date.now() - new Date(started).getTime();
            return ageMs > 10 * 60 * 1000;
          })())) && (
            <button
              onClick={(e) => { e.stopPropagation(); onRetry?.(campaign); }}
              className="flex-1 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5"
              title={generation_status === 'failed'
                ? 'Retry — replay generation using saved request snapshot'
                : 'This campaign has been generating for >10 min. Click to retry.'}
              data-testid={`campaign-retry-${id}`}
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {generation_status === 'failed' ? 'Retry Generation' : 'Retry (Stuck)'}
            </button>
          )}
          {(generation_status === 'queued' || generation_status === 'generating') && onKill && (
            <button
              onClick={(e) => { e.stopPropagation(); onKill?.(campaign); }}
              className="flex-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5"
              title="Force-kill this stuck campaign (marks as failed so you can retry cleanly)"
              data-testid={`campaign-kill-${id}`}
            >
              <XCircle className="w-3.5 h-3.5" />
              Kill Stuck
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onView?.(campaign); }}
            className="flex-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5"
            data-testid={`campaign-view-details-${id}`}
          >
            <Eye className="w-3.5 h-3.5" />
            View Details
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit?.(campaign); }}
            className={`flex-1 px-3 py-2 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 ${theme.button}`}
            data-testid={`campaign-edit-${id}`}
          >
            <Edit3 className="w-3.5 h-3.5" />
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate?.(campaign); }}
            className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 ${theme.buttonLight}`}
            title="Regenerate"
            data-testid={`campaign-regenerate-${id}`}
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFavorite?.(campaign, !isFavorite); }}
            className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors flex items-center justify-center ${
              isFavorite
                ? 'bg-yellow-400 hover:bg-yellow-500 text-white'
                : 'bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-500/10 dark:hover:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400'
            }`}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            data-testid={`campaign-favorite-${id}`}
          >
            <Star className={`w-3.5 h-3.5 ${isFavorite ? 'fill-current' : ''}`} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete?.(campaign); }}
            className="px-3 py-2 bg-rose-50 hover:bg-rose-100 dark:bg-rose-500/10 dark:hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 text-xs font-medium rounded-lg transition-colors flex items-center justify-center"
            title="Delete"
            data-testid={`campaign-delete-${id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignCardV2;
